// A1 : fiche d'une installation (M02, charte D-038). En tête, ce qui demande une action (soumission,
// validation, rejet à corriger, doublon possible, stocks non déclarés) ; puis trois onglets : vue d'ensemble
// (réservoirs en cuves, localisation avec carte, identité, doublons), historique des déclarations
// (indisponible au lot 1, aucune déclaration n'existe) et documents.

import { useState, type FormEvent } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CODES_PROVINCES } from '@carburanttrack/shared';
import { api } from '../../api/client';
import type { InstallationFiche } from '../../api/types';
import { CarteProvinces } from '../../composants/CarteProvinces';
import { Indisponible, Message, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { classeEtat, dateHeure, libelleCategorie, libelleConfidentialite, libelleEtat, libelleProvince } from '../../composants/format';
import { Cuve } from '../../composants/Visualisations';
import { useCarte } from '../../api/requetes';
import { useProduits } from '../../api/requetes';
import { DocumentsObjet } from '../documents/Documents';

type Onglet = 'vue' | 'historique' | 'documents';
const ONGLETS: { cle: Onglet; libelle: string }[] = [
  { cle: 'vue', libelle: 'Vue d’ensemble' },
  { cle: 'historique', libelle: 'Historique des déclarations' },
  { cle: 'documents', libelle: 'Documents' },
];

interface PointFiche {
  cle: string;
  ton: 'ambre' | 'rouge' | 'insuffisante' | 'neutre';
  marque: string;
  titre: string;
  detail: string;
  lien?: string;
}

export function FicheInstallation() {
  const { id } = useParams();
  const lieu = useLocation();
  const client = useQueryClient();
  const fiche = useQuery({ queryKey: ['installation', id], queryFn: async () => (await api<InstallationFiche>(`/api/v1/installations/${id}`)).donnees });
  const carte = useCarte();
  const produits = useProduits();
  const [onglet, setOnglet] = useState<Onglet>('vue');
  const [message, setMessage] = useState<string | null>((lieu.state as { creee?: boolean } | null)?.creee ? 'Installation créée à l’état brouillon.' : null);
  const [motifRejet, setMotifRejet] = useState('');

  const action = useMutation({
    mutationFn: async ({ chemin, corps, succes }: { chemin: string; corps?: unknown; succes: string }) => {
      await api(chemin, { methode: 'POST', corps: corps ?? {} });
      return succes;
    },
    onSuccess: async (succes) => {
      setMessage(succes);
      setMotifRejet('');
      await client.invalidateQueries({ queryKey: ['installation', id] });
      await client.invalidateQueries({ queryKey: ['installations'] });
      await client.invalidateQueries({ queryKey: ['accueil-situation'] });
    },
  });

  if (fiche.isLoading) return <Squelette lignes={10} />;
  if (fiche.error) return <MessageErreur erreur={fiche.error} titre="Installation introuvable ou non autorisée" />;
  const f = fiche.data!;
  const nomProduit = (code: string) => produits.data?.find((p) => p.code === code)?.nom ?? code;
  const aSoumettre = f.droits.modifier && f.etatValidation === 'brouillon';
  const aValider = f.droits.valider && f.etatValidation === 'en_attente_validation';

  const points: PointFiche[] = [];
  if (f.motifRejet && f.etatValidation === 'brouillon')
    points.push({ cle: 'rejet', ton: 'rouge', marque: 'Rejet, à corriger', titre: 'Dernière demande de validation rejetée', detail: f.motifRejet });
  if (f.etatValidation === 'en_attente_validation' && !aValider)
    points.push({ cle: 'attente', ton: 'ambre', marque: 'Validation, en attente', titre: 'Installation en attente de validation', detail: f.soumisLe ? `Soumise le ${dateHeure(f.soumisLe)}, validation par l’administrateur national` : 'Validation par l’administrateur national' });
  for (const d of f.doublonsPotentiels)
    points.push({ cle: `doublon-${d.id}`, ton: 'neutre', marque: 'Référentiel, doublon possible', titre: d.nom, detail: `À ${d.distanceMetres} m, ${libelleEtat(d.etatValidation).toLowerCase()}`, lien: `/referentiel/installations/${d.id}` });
  if (f.reservoirs && f.reservoirs.length > 0)
    points.push({ cle: 'stocks', ton: 'insuffisante', marque: 'Information insuffisante, stocks', titre: 'Aucune déclaration de stock reçue', detail: 'Information insuffisante, pas une gravité' });

  return (
    <div className="fiche-installation">
      <div className="page-tete">
        <div>
          <h1>{f.nom}</h1>
          <div className="actions">
            <span className="attenue">
              {[libelleCategorie(f.categorie), f.organisationNom, f.localiteNom, f.provinceNom ?? libelleProvince(f.provinceCode)].filter(Boolean).join(', ')}
            </span>
            <span className={classeEtat(f.etatValidation)}>{libelleEtat(f.etatValidation)}</span>
            <span className="etiquette">{libelleConfidentialite(f.confidentialite)}</span>
            {f.estFictive && <span className="etiquette gris">données fictives</span>}
          </div>
        </div>
        {aSoumettre && (
          <button type="button" className="bouton primaire" disabled={action.isPending} onClick={() => action.mutate({ chemin: `/api/v1/installations/${f.id}/soumission`, succes: 'Installation soumise à validation.' })}>
            Soumettre à validation
          </button>
        )}
      </div>

      {message && <Message type="succes">{message}</Message>}
      {action.error && <MessageErreur erreur={action.error} titre="Action refusée" />}

      {aValider && (
        <section className="a-faire" aria-labelledby="titre-validation">
          <div>
            <span className="sur-titre">À faire maintenant</span>
            <h2 id="titre-validation">Valider ou rejeter cette installation</h2>
            <p>{f.soumisLe ? `Soumise le ${dateHeure(f.soumisLe)}. ` : ''}Un rejet renvoie l’installation à l’état brouillon avec son motif.</p>
          </div>
          <div className="actions decision">
            <button type="button" className="bouton primaire" disabled={action.isPending} onClick={() => action.mutate({ chemin: `/api/v1/installations/${f.id}/validation`, corps: { decision: 'valider' }, succes: 'Installation validée.' })}>
              Valider
            </button>
            <label htmlFor="motif-rejet" className="visuellement-cache">
              Motif du rejet
            </label>
            <input id="motif-rejet" placeholder="Motif du rejet" value={motifRejet} onChange={(e) => setMotifRejet(e.target.value)} />
            <button
              type="button"
              className="bouton danger"
              disabled={action.isPending || motifRejet.trim().length < 3}
              onClick={() => action.mutate({ chemin: `/api/v1/installations/${f.id}/validation`, corps: { decision: 'rejeter', motif: motifRejet }, succes: 'Installation renvoyée à l’état brouillon.' })}
            >
              Rejeter
            </button>
          </div>
        </section>
      )}

      {points.length > 0 && (
        <section className="a-traiter" aria-labelledby="titre-attention">
          <h2 id="titre-attention" className="sur-titre">
            Ce qui demande une action
          </h2>
          <ol className="liste-exceptions">
            {points.map((p, i) => {
              const contenu = (
                <>
                  <span className="numero" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">{p.marque}</span>
                    <b>{p.titre}</b>
                    <small>{p.detail}</small>
                  </span>
                  {p.lien ? <Icone nom="chevron" /> : <span />}
                </>
              );
              return (
                <li key={p.cle}>
                  {p.lien ? (
                    <Link className={`exception ${p.ton}`} to={p.lien}>
                      {contenu}
                    </Link>
                  ) : (
                    <div className={`exception ${p.ton}`}>{contenu}</div>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      )}

      <div className="onglets" role="tablist" aria-label="Sections de la fiche">
        {ONGLETS.map((o) => (
          <button key={o.cle} type="button" role="tab" id={`onglet-fiche-${o.cle}`} aria-selected={onglet === o.cle} aria-controls="panneau-fiche" onClick={() => setOnglet(o.cle)}>
            {o.libelle}
          </button>
        ))}
      </div>

      <div id="panneau-fiche" role="tabpanel" aria-labelledby={`onglet-fiche-${onglet}`}>
        {onglet === 'vue' && (
          <div className="deux-colonnes">
            <div className="principal">
              <section className="carte-bloc" aria-labelledby="t-reservoirs">
                <div className="carte-bloc-tete">
                  <h2 id="t-reservoirs">Réservoirs</h2>
                  <span className="attenue">Dernière déclaration : aucune</span>
                </div>
                {f.reservoirs === null ? (
                  <Indisponible motif={f.reservoirsMotif ?? 'réservoirs non visibles'} />
                ) : f.reservoirs.length === 0 ? (
                  <p className="attenue">Aucun réservoir enregistré.</p>
                ) : (
                  <>
                    <div className="cuves">
                      {f.reservoirs.map((r) => (
                        <Cuve key={r.id} produit={`${r.numero}, ${nomProduit(r.produitCode)}`} capacite={r.capaciteNominale} unite={r.unite} volume={null} fraicheur={null} motifIndisponible="aucune déclaration de stock reçue" />
                      ))}
                    </div>
                    <div className="legende-hachure">
                      <span className="echantillon" aria-hidden="true" />
                      Hachuré : information insuffisante. Ce n’est pas une gravité : les cuves n’ont simplement pas été déclarées.
                    </div>
                  </>
                )}
                {f.droits.creerReservoir && <FormulaireReservoir installationId={f.id} />}
              </section>

              {f.doublonsPotentiels.length > 0 && (
                <section className="carte-bloc" aria-labelledby="t-doublons">
                  <h2 id="t-doublons">Doublons potentiels</h2>
                  <ul className="liste-doublons">
                    {f.doublonsPotentiels.map((d) => (
                      <li key={d.id}>
                        <span>
                          <Link to={`/referentiel/installations/${d.id}`}>{d.nom}</Link>
                          <small>
                            à {d.distanceMetres} m, {libelleEtat(d.etatValidation).toLowerCase()}
                          </small>
                        </span>
                        {f.droits.valider && <FusionVers sourceId={f.id} cible={d} />}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            <aside className="lateral">
              <section className="carte-bloc" aria-labelledby="t-localisation">
                <h2 id="t-localisation">Localisation</h2>
                {carte.data && (
                  <div className="carte-localisation">
                    <CarteProvinces
                      carte={carte.data}
                      points={[{ id: f.id, nom: f.nom, longitude: f.longitude, latitude: f.latitude, categorie: f.categorie }]}
                      pointMisEnAvant={f.id}
                      selection={f.provinceCode}
                      titre={`Localisation de ${f.nom}`}
                      legende={false}
                    />
                  </div>
                )}
                <dl className="paires">
                  <div>
                    <dt>Province</dt>
                    <dd>{libelleProvince(f.provinceCode)}</dd>
                  </div>
                  <div>
                    <dt>Département</dt>
                    <dd>{f.departementNom ?? <span className="attenue">non rattaché</span>}</dd>
                  </div>
                  <div>
                    <dt>Localité la plus proche</dt>
                    <dd>{f.localiteNom ?? <span className="attenue">aucune</span>}</dd>
                  </div>
                  <div>
                    <dt>Coordonnées</dt>
                    <dd>
                      latitude {f.latitude.toFixed(5).replace('.', ',')}
                      <small>longitude {f.longitude.toFixed(5).replace('.', ',')}</small>
                    </dd>
                  </div>
                  <div>
                    <dt>Rattachement</dt>
                    <dd>
                      {f.rattachementManuel ? 'manuel' : 'automatique'}
                      {f.motifRattachement && <small className="attenue bloc">{f.motifRattachement}</small>}
                    </dd>
                  </div>
                </dl>
                {f.droits.modifier && <FormulaireRattachement fiche={f} />}
              </section>

              <section className="carte-bloc" aria-labelledby="t-identite">
                <h2 id="t-identite">Identité</h2>
                <dl className="paires">
                  <div>
                    <dt>Organisation</dt>
                    <dd>
                      <Link to={`/referentiel/organisations/${f.organisationId}`}>{f.organisationNom}</Link>
                    </dd>
                  </div>
                  <div>
                    <dt>Adresse</dt>
                    <dd>{f.adresse ?? <span className="indisponible">non renseignée</span>}</dd>
                  </div>
                  <div>
                    <dt>Horaires</dt>
                    <dd>{f.horaires ?? <span className="indisponible">non renseignés</span>}</dd>
                  </div>
                  <div>
                    <dt>Soumise le</dt>
                    <dd>{f.soumisLe ? dateHeure(f.soumisLe) : <span className="attenue">non soumise</span>}</dd>
                  </div>
                  <div>
                    <dt>Validée le</dt>
                    <dd>{f.valideLe ? dateHeure(f.valideLe) : <span className="attenue">non validée</span>}</dd>
                  </div>
                </dl>
              </section>
            </aside>
          </div>
        )}

        {onglet === 'historique' && (
          <section className="carte-bloc" aria-labelledby="t-historique">
            <h2 id="t-historique">Historique des déclarations</h2>
            <Indisponible motif="aucune déclaration de stock reçue : le circuit des déclarations n’est pas encore en service" />
          </section>
        )}

        {onglet === 'documents' && (
          <section className="carte-bloc" aria-labelledby="t-documents">
            <h2 id="t-documents">Documents</h2>
            <DocumentsObjet objetType="installation" objetId={f.id} peutDeposer={f.droits.deposerDocument} />
          </section>
        )}
      </div>
    </div>
  );
}

function FormulaireRattachement({ fiche }: { fiche: InstallationFiche }) {
  const client = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [province, setProvince] = useState(fiche.provinceCode ?? 'GA-1');
  const [departement, setDepartement] = useState(fiche.departementId ?? '');
  const [motif, setMotif] = useState('');
  const departements = useQuery({
    queryKey: ['departements', province],
    queryFn: async () => (await api<{ id: string; nom: string }[]>(`/api/v1/departements?province=${province}`)).donnees,
    enabled: ouvert,
  });
  const envoi = useMutation({
    mutationFn: () => api(`/api/v1/installations/${fiche.id}/rattachement`, { methode: 'PATCH', corps: { provinceCode: province, departementId: departement || null, motif } }),
    onSuccess: async () => {
      setOuvert(false);
      setMotif('');
      await client.invalidateQueries({ queryKey: ['installation', fiche.id] });
    },
  });
  if (!ouvert)
    return (
      <button type="button" className="bouton espace-haut" onClick={() => setOuvert(true)}>
        Corriger le rattachement
      </button>
    );
  return (
    <form
      className="espace-haut"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <div className="champ">
        <label htmlFor="r-province">Province</label>
        <select
          id="r-province"
          value={province}
          onChange={(e) => {
            setProvince(e.target.value);
            setDepartement('');
          }}
        >
          {CODES_PROVINCES.map((c) => (
            <option key={c} value={c}>
              {libelleProvince(c)}
            </option>
          ))}
        </select>
      </div>
      <div className="champ">
        <label htmlFor="r-departement">Département</label>
        <select id="r-departement" value={departement} onChange={(e) => setDepartement(e.target.value)}>
          <option value="">Aucun (commune hors département)</option>
          {departements.data?.map((d) => (
            <option key={d.id} value={d.id}>
              {d.nom}
            </option>
          ))}
        </select>
      </div>
      <div className="champ">
        <label htmlFor="r-motif">Motif (obligatoire)</label>
        <input id="r-motif" value={motif} onChange={(e) => setMotif(e.target.value)} />
      </div>
      {envoi.error && <MessageErreur erreur={envoi.error} />}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={motif.trim().length < 3 || envoi.isPending}>
          Enregistrer le rattachement
        </button>
        <button type="button" className="bouton" onClick={() => setOuvert(false)}>
          Annuler
        </button>
      </div>
    </form>
  );
}

function FusionVers({ sourceId, cible }: { sourceId: string; cible: { id: string; nom: string } }) {
  const client = useQueryClient();
  const [motif, setMotif] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const fusion = useMutation({
    mutationFn: () => api(`/api/v1/installations/${sourceId}/fusion`, { corps: { cibleId: cible.id, motif } }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['installation'] });
      await client.invalidateQueries({ queryKey: ['installations'] });
      await client.invalidateQueries({ queryKey: ['doublons'] });
    },
  });
  if (fusion.isSuccess) return <Message type="succes">Installation fusionnée dans « {cible.nom} » et archivée.</Message>;
  if (!ouvert)
    return (
      <button type="button" className="bouton" onClick={() => setOuvert(true)}>
        Fusionner dans celle-ci
      </button>
    );
  return (
    <span className="actions">
      <label htmlFor={`fusion-${cible.id}`} className="visuellement-cache">
        Motif de la fusion
      </label>
      <input id={`fusion-${cible.id}`} className="champ-motif" placeholder="Motif de la fusion" value={motif} onChange={(e) => setMotif(e.target.value)} />
      <button type="button" className="bouton danger" disabled={motif.trim().length < 3 || fusion.isPending} onClick={() => fusion.mutate()}>
        Confirmer la fusion
      </button>
      {fusion.error && <MessageErreur erreur={fusion.error} />}
    </span>
  );
}

function FormulaireReservoir({ installationId }: { installationId: string }) {
  const client = useQueryClient();
  const produits = useProduits();
  const [ouvert, setOuvert] = useState(false);
  const [numero, setNumero] = useState('');
  const [produit, setProduit] = useState('gasoil');
  const [capacite, setCapacite] = useState('');
  const [nonPompable, setNonPompable] = useState('0');
  const estGpl = produits.data?.find((p) => p.code === produit)?.estGpl ?? false;
  const envoi = useMutation({
    mutationFn: () => api('/api/v1/reservoirs', { corps: { installationId, numero, produitCode: produit, capaciteNominale: capacite, volumeNonPompable: nonPompable, unite: estGpl ? 'kg' : 'L' } }),
    onSuccess: async () => {
      setNumero('');
      setCapacite('');
      setNonPompable('0');
      await client.invalidateQueries({ queryKey: ['installation', installationId] });
    },
  });
  if (!ouvert)
    return (
      <button type="button" className="bouton espace-haut" onClick={() => setOuvert(true)}>
        <Icone nom="ajout" />
        Ajouter un réservoir
      </button>
    );
  return (
    <form
      className="espace-haut"
      onSubmit={(e) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h3>Ajouter un réservoir</h3>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="res-numero">Numéro</label>
          <input id="res-numero" value={numero} onChange={(e) => setNumero(e.target.value)} />
        </div>
        <div className="champ">
          <label htmlFor="res-produit">Produit</label>
          <select id="res-produit" value={produit} onChange={(e) => setProduit(e.target.value)}>
            {produits.data?.map((p) => (
              <option key={p.code} value={p.code}>
                {p.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="res-capacite">Capacité nominale ({estGpl ? 'kg' : 'L'})</label>
          <input id="res-capacite" inputMode="decimal" value={capacite} onChange={(e) => setCapacite(e.target.value.replace(',', '.'))} />
        </div>
        <div className="champ">
          <label htmlFor="res-np">Volume non pompable ({estGpl ? 'kg' : 'L'})</label>
          <input id="res-np" inputMode="decimal" value={nonPompable} onChange={(e) => setNonPompable(e.target.value.replace(',', '.'))} />
        </div>
      </div>
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Réservoir refusé" />}
      {envoi.isSuccess && <Message type="succes">Réservoir ajouté.</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={!numero || !capacite || envoi.isPending}>
          Ajouter le réservoir
        </button>
        <button type="button" className="bouton" onClick={() => setOuvert(false)}>
          Fermer
        </button>
      </div>
    </form>
  );
}
